/**
 * Local entitlement cache + paywall gate.
 *
 * Source of truth: `users.students_enabled` on Supabase.
 *   - `students_enabled = -1`  → Pro tier (unlimited students + AI).
 *   - `students_enabled = 2`   → Free tier default (2 students cap).
 *
 * The `useProAccess` hook returns the current access state and
 * triggers a re-read when the user / session changes. The
 * source-of-truth check (`readAccessState`) is the Supabase
 * read the React layer calls; it lives in the same module so
 * the consumer can call it without dragging in the hook.
 *
 * v1.0-D wires the canonical Free-vs-Pro AccessState shape that
 * the CaseloadScreen + PaywallScreen + Edge Functions all share:
 *
 *   {
 *     kind: 'pro',
 *     isPro: true,
 *     studentsLimit: Infinity,
 *     aiGenerationsLimit: Infinity,
 *     aiGenerationsUsed: 0,
 *     aiGenerationsRemaining: Infinity,
 *     productId: 'effectiveslp_pro',
 *     expiresAt: string | null,
 *   }
 *
 *   {
 *     kind: 'free',
 *     isPro: false,
 *     studentsLimit: 2,
 *     aiGenerationsLimit: 10,
 *     aiGenerationsUsed: number,
 *     aiGenerationsRemaining: number,
 *   }
 *
 *   {
 *     kind: 'free-exhausted',  // students cap hit
 *     isPro: false,
 *     studentsLimit: 2,
 *     aiGenerationsUsed: number,
 *   }
 *
 *   { kind: 'unknown' }  // no session or read failed
 */
import { useEffect, useState } from 'react';
import { supabase } from '../supabase/client';

/** Mirror of v1.0-A.0 schema default. Re-used by the webhook. */
export const PRO_ENTITLEMENT_ID = 'effectiveslp_pro';
export const FREE_TIER_STUDENTS_ENABLED = 2;
export const FREE_TIER_AI_GENERATIONS_LIMIT = 10;
export const PRO_STUDENTS_ENABLED = -1;

export interface UserEntitlementRow {
  id: string;
  students_enabled: number;
  ai_consent_granted_at: string | null;
  onboarding_completed_at: string | null;
  full_name: string | null;
  license_number: string | null;
}

/**
 * The canonical access-state surface. Every consumer (Caseload
 * "Upgrade to Pro" CTA, PaywallScreen, Edge Function rate-limit
 * error envelope) reads from this shape. Avoids per-screen
 * reshaping of the underlying `users` row.
 */
export type AccessState =
  | {
      kind: 'pro';
      isPro: true;
      productId: string;
      expiresAt: string | null;
      studentsLimit: number;
      aiGenerationsLimit: number;
      aiGenerationsUsed: number;
      aiGenerationsRemaining: number;
    }
  | {
      kind: 'free';
      isPro: false;
      studentsUsed: number;
      studentsLimit: number;
      aiGenerationsLimit: number;
      aiGenerationsUsed: number;
      aiGenerationsRemaining: number;
    }
  | {
      kind: 'free-exhausted';
      isPro: false;
      studentsUsed: number;
      studentsLimit: number;
      aiGenerationsLimit: number;
      aiGenerationsUsed: number;
      aiGenerationsRemaining: number;
    }
  | { kind: 'unknown' };

/**
 * STAGING DEMO BYPASS — controlled via `VITE_BYPASS_ENTITLEMENT=true`.
 *
 * When this flag is set, every signed-in user is presented with the
 * Pro tier UI regardless of their real `users.students_enabled`
 * value. This exists because the iOS RevenueCat integration does
 * NOT yet identify users with their Supabase `auth.uid()` at the
 * SDK-init step — purchases land as anonymous RevenueCat users,
 * the `webhooks-revenuecat` Edge Function rejects the events with
 * `app_user_id_not_uuid` (HTTP 400), and `users.students_enabled`
 * stays at the Free default (2).
 *
 * Tracking tickets:
 *   - #569 — iOS: wire `PurchasesService.logIn(userId:)` after sign-in
 *   - #570 — webhook: handle anonymous IDs / migrate existing buyers
 *
 * REMOVE this block once both tickets land and a real Pro purchase
 * has been observed flipping `users.students_enabled` to -1.
 *
 * The flag is a Vite-time constant (compiled into the bundle), so
 * a `false` in `.env.example` plus an explicit `true` in the
 * staging Pages project is the safe shape — production builds
 * default to false.
 */
function isEntitlementBypassed(): boolean {
  const v = import.meta.env.VITE_BYPASS_ENTITLEMENT as string | undefined;
  return v === 'true' || v === '1';
}

/**
 * Reads the current user's entitlement state from Supabase. Pure
 * read — does not increment counters or call external APIs.
 *
 * `students_enabled = -1` is the magic Pro value and bypasses
 * the soft cap. The AI generation count is sourced from
 * `users.ai_generations_count` (or the `ai_generation_log` row
 * count for the current UTC month — see _shared/rateLimit.ts in
 * the Edge Function). The client uses the cached column for
 * snappy UI; the Edge Function is the source of truth for the
 * rate-limit verdict.
 */
export async function readAccessState(): Promise<AccessState> {
  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData.session;
  if (!session) return { kind: 'unknown' };
  const userId = session.user.id;

  // See the bypass comment above. Runs before any Supabase read
  // so the demo never depends on the (broken) iOS RevenueCat path.
  if (isEntitlementBypassed()) {
    return {
      kind: 'pro',
      isPro: true,
      productId: PRO_ENTITLEMENT_ID,
      expiresAt: null,
      studentsLimit: Number.POSITIVE_INFINITY,
      aiGenerationsLimit: Number.POSITIVE_INFINITY,
      aiGenerationsUsed: 0,
      aiGenerationsRemaining: Number.POSITIVE_INFINITY,
    };
  }

  const [
    { count: studentsCount },
    { data: userRow },
  ] = await Promise.all([
    supabase
      .from('students')
      .select('id', { count: 'exact', head: true })
      .eq('owner_id', userId)
      .eq('archived', false),
    supabase
      .from('users')
      .select(
        'id, students_enabled, ai_consent_granted_at, onboarding_completed_at, full_name, license_number',
      )
      .eq('id', userId)
      .maybeSingle(),
  ]);

  const row = userRow as UserEntitlementRow | null;
  const cap = row?.students_enabled ?? FREE_TIER_STUDENTS_ENABLED;
  const used = studentsCount ?? 0;
  // The free-tier AI generation count is sourced from the cached
  // column on the user row (mirrors the Edge Function's
  // ai_generation_log COUNT). The Edge Function is the source of
  // truth for the rate-limit verdict; this is the UI hint.
  const aiUsed = await readMonthlyAiGenerationCount(userId);
  const aiLimit = FREE_TIER_AI_GENERATIONS_LIMIT;
  const aiRemaining = Math.max(0, aiLimit - aiUsed);

  // Pro: unlimited students + unlimited AI generations.
  if (cap === PRO_STUDENTS_ENABLED) {
    return {
      kind: 'pro',
      isPro: true,
      productId: PRO_ENTITLEMENT_ID,
      expiresAt: null,
      studentsLimit: Number.POSITIVE_INFINITY,
      aiGenerationsLimit: Number.POSITIVE_INFINITY,
      aiGenerationsUsed: aiUsed,
      aiGenerationsRemaining: Number.POSITIVE_INFINITY,
    };
  }

  if (used >= cap) {
    return {
      kind: 'free-exhausted',
      isPro: false,
      studentsUsed: used,
      studentsLimit: cap,
      aiGenerationsLimit: aiLimit,
      aiGenerationsUsed: aiUsed,
      aiGenerationsRemaining: aiRemaining,
    };
  }
  return {
    kind: 'free',
    isPro: false,
    studentsUsed: used,
    studentsLimit: cap,
    aiGenerationsLimit: aiLimit,
    aiGenerationsUsed: aiUsed,
    aiGenerationsRemaining: aiRemaining,
  };
}

/**
 * Counts the AI generation rows for the current UTC month for
 * the given user. Mirrors the Edge Function's rate-limit query
 * (see supabase/functions/_shared/rateLimit.ts). Uses the same
 * UTC-month start convention so the UI badge stays in lock-step
 * with what the server enforces.
 */
async function readMonthlyAiGenerationCount(userId: string): Promise<number> {
  const monthStart = currentMonthStartUtc();
  const { count, error } = await supabase
    .from('ai_generation_log')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .gte('created_at', monthStart);
  if (error) {
    // Conservative: on a read failure, render the count as 0
    // (the server still enforces the cap).
    return 0;
  }
  return count ?? 0;
}

/**
 * UTC-month start ISO string. Mirrors the Edge Function helper
 * so the UI's "X of 10 used" badge matches what the server sees.
 */
function currentMonthStartUtc(): string {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  return year + '-' + month + '-01T00:00:00Z';
}

/**
 * React-side mirror. Subscribes to auth-state changes so the
 * hook re-reads when the user signs in / out. Returns the same
 * `AccessState` shape as the imperative helper.
 *
 * Note: in v1.0-D the hook does NOT subscribe to a RevenueCat
 * customer-info broadcast (the iOS-native SDK event isn't
 * reachable from the WebView). The webhook mirroring flow
 * (Pro purchase → webhook → UPDATE users.students_enabled) is
 * the source of truth; a real-time refresh on the client comes
 * from a manual re-read after a successful purchase (the
 * PaywallScreen calls `refresh()` once the purchase resolves).
 */
export function useProAccess(): {
  state: AccessState;
  refresh: () => Promise<void>;
} {
  const [state, setState] = useState<AccessState>({ kind: 'unknown' });

  async function refresh(): Promise<void> {
    try {
      const next = await readAccessState();
      setState(next);
    } catch {
      setState({ kind: 'unknown' });
    }
  }

  useEffect(() => {
    let cancelled = false;
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      void (async () => {
        if (cancelled) return;
        await refresh();
      })();
    });
    void (async () => {
      if (cancelled) return;
      await refresh();
    })();
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  return { state, refresh };
}
